import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { findDOMNode } from 'react-dom';

import cls from 'classnames';

import { format } from 'date-fns';
import noop from 'lodash/noop';

import { lang } from '@ott/l10n';
import { getBeginOfDateInUTC } from '@ott/utility-date';

import DatesContainer from './blocks/DatesContainer/DatesContainer';
import MonthsNavigation from './blocks/MonthsNavigation/MonthsNavigation';
import Time from './blocks/Time/Time';
import { ANIMATION_TIME, DAY_IN_WEEK, MONTH_SCROLL_OFFSET } from './calendarConstants';
import { DEFAULT_WEEK_PARAMS } from './weekParamsByLocale';

import { Calendar as CalendarMaker } from 'calendar-base';
import scrollIntoView from 'scroll-into-view';

import styles from './Calendar.scss';

const calendarMaker = new CalendarMaker({
  weekStart: DEFAULT_WEEK_PARAMS.calendarBaseWeekStart,
});

const monthTitleFormatter = Intl.DateTimeFormat(lang, {
  month: 'long',
  timeZone: 'UTC',
});

const mainLocator = 'Calendar';

class Calendar extends PureComponent {
  static propTypes = {
    startDate: PropTypes.instanceOf(Date),
    endDate: PropTypes.instanceOf(Date),
    hoveredDate: PropTypes.instanceOf(Date),

    minDate: PropTypes.instanceOf(Date),
    maxDate: PropTypes.instanceOf(Date),
    maxDays: PropTypes.number,

    endOnlyAfterStart: PropTypes.bool,
    pickOneDate: PropTypes.bool,
    differentDates: PropTypes.bool,
    scrollToWeek: PropTypes.bool,

    heatmap: PropTypes.shape({
      currency: PropTypes.string.isRequired,
      min: PropTypes.number.isRequired,
      mid: PropTypes.number.isRequired,
      max: PropTypes.number.isRequired,
      time: PropTypes.number.isRequired,
      prices: PropTypes.PropTypes.object.isRequired,
    }),
    heatmapStatus: PropTypes.shape({
      initial: PropTypes.bool.isRequired,
      loading: PropTypes.bool.isRequired,
      success: PropTypes.bool.isRequired,
      error: PropTypes.bool.isRequired,
      cancelled: PropTypes.bool.isRequired,
    }),
    withHeatmap: PropTypes.bool,
    showHeatmapPrices: PropTypes.bool,
    showHeatmapLegend: PropTypes.bool,
    isDirectOnly: PropTypes.bool,
    onDirectionSwitch: PropTypes.func,
    checkIfDateIsDisabled: PropTypes.func,

    timeField: PropTypes.shape({
      input: PropTypes.object,
    }),
    onTimeHover: PropTypes.func,
    onTimeSelect: PropTypes.func,

    onDateHover: PropTypes.func,
    onDateSelect: PropTypes.func,

    hideMonthsNavigation: PropTypes.bool,
    showYear: PropTypes.bool,
    startDateIsFixed: PropTypes.bool,

    specificLocators: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  };

  static defaultProps = {
    onDateHover: noop,
    onDateSelect: noop,
    specificLocators: [],
    startDateIsFixed: false,
  };

  constructor(props, ctx) {
    super(props, ctx);

    this.state = {
      scrollTop: 0,
      datesContainerHeight: 0, // используется для определения высоты блока навигации
      months: this.getCalendarData(props),
    };
  }

  isHeatmapChanged(oldHeatmap, newHeatmap) {
    if (oldHeatmap && newHeatmap) {
      return oldHeatmap.time !== newHeatmap.time;
    }

    return oldHeatmap !== newHeatmap;
  }

  componentWillReceiveProps(nextProps) {
    const datesChanged =
      this.props.minDate !== nextProps.minDate || this.props.maxDate !== nextProps.maxDate;
    const heatmapChanged = this.isHeatmapChanged(this.props.heatmap, nextProps.heatmap);

    if (datesChanged || heatmapChanged) {
      this.setState({
        months: this.getCalendarData(nextProps),
      });
    }
  }

  getDayWithHoliday(day) {
    const { holidays } = this.props;
    let isHoliday;

    if (lang === 'ru' && holidays) {
      const formatDate = format(new Date(day.year, day.month, day.day), 'yyyy-MM-dd');
      isHoliday = holidays[formatDate];
    } else {
      isHoliday = day.weekDay === 0 || day.weekDay === 6;
    }

    return {
      ...day,
      isHoliday,
    };
  }

  groupDaysByWeek = (acc, day, idx) => {
    const weekIdx = Math.floor(idx / DAY_IN_WEEK);

    if (!acc[weekIdx]) {
      acc[weekIdx] = [];
    }

    if (day) {
      day = this.getDayWithHoliday(day);
    }

    acc[weekIdx].push(day);
    return acc;
  };

  getWeeks(days) {
    return days.reduce(this.groupDaysByWeek, []).map((week) => week);
  }

  getCalendarData(props) {
    let { minDate, maxDate } = props;

    let months = [];
    if (minDate && maxDate) {
      const MONTHS_IN_YEAR = 12;
      const minDateYear = Number(minDate.getFullYear());
      const maxDateYear = Number(maxDate.getFullYear());
      const minDateMonths = Number(minDate.getMonth());
      const maxDateMonths =
        (maxDateYear - minDateYear) * MONTHS_IN_YEAR + Number(maxDate.getMonth());

      for (let month = minDateMonths; month <= maxDateMonths; month++) {
        const addingYear = Math.floor(month / MONTHS_IN_YEAR);
        const date = new Date(addingYear + minDateYear, month - addingYear * MONTHS_IN_YEAR, 1);
        const monthDays = calendarMaker.getCalendar(date.getFullYear(), date.getMonth());

        months.push({
          idx: date.getMonth(),
          year: date.getFullYear(),
          title: monthTitleFormatter.format(getBeginOfDateInUTC(date)),
          weeks: this.getWeeks(monthDays),
        });
      }
    }

    return months;
  }

  handleNavigationMonthClick = ({ year, idx }, callback) => {
    const monthRef = this.datesContainerRef[`month-${idx}${year}-ref`];
    const monthNode = findDOMNode(monthRef);
    const isLast = monthNode.parentNode.lastChild === monthNode;
    const scrolledAreaNode = findDOMNode(this.datesContainerRef.getScrollAreaRef());

    scrollIntoView(
      monthNode,
      {
        time: ANIMATION_TIME,
        align: {
          top: 0,
          topOffset: isLast ? 0 : MONTH_SCROLL_OFFSET,
        },
        validTarget: (target) => target === scrolledAreaNode,
      },
      () => callback()
    );
  };

  handleDateSelect = (selectedDate) => {
    const { startDate, endDate, pickOneDate, differentDates, onDateSelect, startDateIsFixed } =
      this.props;

    if (pickOneDate) {
      onDateSelect(selectedDate, selectedDate); // договоренность
    } else {
      if (startDate && !endDate) {
        if (startDate < selectedDate) {
          onDateSelect(startDate, selectedDate);
        } else if (selectedDate < startDate) {
          onDateSelect(selectedDate, startDate);
        } else {
          if (!differentDates) {
            // допускается выбор одинаковых дат для start & end
            onDateSelect(startDate, selectedDate);
          }
        }
      } else {
        if (startDateIsFixed) {
          onDateSelect(startDate, selectedDate);
        } else {
          onDateSelect(selectedDate);
        }
      }
    }
  };

  handleDatesScroll = (e) => {
    this.setState({ scrollTop: e.target.scrollTop });
  };

  render() {
    const { months, scrollTop } = this.state;

    const {
      startDate,
      endDate,
      minDate,
      maxDate,
      hoveredDate,
      onDateHover,
      maxDays,
      endOnlyAfterStart,
      pickOneDate,
      heatmap,
      heatmapStatus,
      withHeatmap,
      showHeatmapLegend,
      showHeatmapPrices,
      isDirectOnly,
      onDirectionSwitch,
      checkIfDateIsDisabled,
      timeField,
      onTimeHover,
      onTimeSelect,
      minTime,
      maxTime,
      hideYearInDatesContainer,
      onDatesContainerLeave,
      onTimeContainerLeave,
      specificLocators,
      hideMonthsNavigation,
      isMobile,
      scrollToWeek,
      showRouteDirections,
      routeDirectionsOptions,
      defaultOptionDirectionIndex,
      onOptionSelect,
    } = this.props;

    return (
      <div
        className={cls(styles.Calendar, {
          [styles['Calendar--withoutMonthsNavigation']]: hideMonthsNavigation,
          [styles['Calendar--hasTime']]: Boolean(timeField),
          [styles['Calendar--isMobile']]: isMobile,
        })}
        data-locator={cls(mainLocator, specificLocators)}
      >
        {!hideMonthsNavigation && (
          <MonthsNavigation
            months={months}
            datesScrollTop={scrollTop}
            onMonthClick={this.handleNavigationMonthClick}
          />
        )}
        <DatesContainer
          ref={(ref) => (this.datesContainerRef = ref)}
          months={months}
          startDate={startDate}
          endDate={endDate}
          endOnlyAfterStart={endOnlyAfterStart}
          pickOneDate={pickOneDate}
          hoveredDate={hoveredDate}
          minDate={minDate}
          maxDate={maxDate}
          maxDays={maxDays}
          weekdays={DEFAULT_WEEK_PARAMS.weekdays}
          hideMonthsNavigation={hideMonthsNavigation}
          hideYearInDatesContainer={hideYearInDatesContainer}
          onContainerLeave={onDatesContainerLeave}
          onDateSelect={this.handleDateSelect}
          onScroll={this.handleDatesScroll}
          onDateHover={onDateHover}
          heatmap={heatmap}
          heatmapStatus={heatmapStatus}
          withHeatmap={withHeatmap}
          showHeatmapLegend={showHeatmapLegend}
          showHeatmapPrices={showHeatmapPrices}
          isDirectOnly={isDirectOnly}
          onDirectionSwitch={onDirectionSwitch}
          checkIfDateIsDisabled={checkIfDateIsDisabled}
          isMobile={isMobile}
          scrollToWeek={scrollToWeek}
          showRouteDirections={showRouteDirections}
          routeDirectionsOptions={routeDirectionsOptions}
          defaultOptionDirectionIndex={defaultOptionDirectionIndex}
          onOptionSelect={onOptionSelect}
        />
        {timeField && (
          <Time
            onContainerLeave={onTimeContainerLeave}
            onTimeHover={onTimeHover}
            selectedTime={timeField.input.value}
            onTimeSelect={onTimeSelect}
            minTime={minTime}
            maxTime={maxTime}
          />
        )}
      </div>
    );
  }
}

export default Calendar;
